import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, useLanguage, API } from "../App";
import { Button } from "../components/ui/button";
import { 
  BarChart3, 
  FileText, 
  Clock, 
  Shield, 
  CheckCircle,
  ArrowRight,
  Building2,
  Users,
  Smartphone,
  Send,
  Loader2
} from "lucide-react";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

export const LandingPage = () => {
  const { user } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [sending, setSending] = useState(false);

  const features = [
    {
      icon: Building2,
      title: language === "sv" ? "Projekthantering" : "Project Management",
      desc: language === "sv" 
        ? "Komplett översikt över alla projekt, etapper och aktiviteter"
        : "Complete overview of all projects, phases and activities"
    },
    {
      icon: Clock,
      title: language === "sv" ? "Tidrapportering" : "Time Reporting",
      desc: language === "sv"
        ? "Rapportera tid på 10 sekunder direkt från mobilen"
        : "Report time in 10 seconds directly from your phone"
    },
    {
      icon: FileText,
      title: language === "sv" ? "ÄTA-hantering" : "Change Orders",
      desc: language === "sv"
        ? "Spårbart flöde för tilläggsarbeten med godkännanden"
        : "Traceable workflow for additional work with approvals"
    },
    {
      icon: Shield,
      title: language === "sv" ? "Arbetsmiljö & KMA" : "Safety & Quality",
      desc: language === "sv"
        ? "Riskbedömningar, avvikelser och incidentrapporter"
        : "Risk assessments, deviations and incident reports"
    },
    {
      icon: CheckCircle,
      title: language === "sv" ? "Egenkontroller" : "Self-inspections",
      desc: language === "sv"
        ? "Digitala checklistor med signering och fotobilagor"
        : "Digital checklists with signing and photo attachments"
    },
    {
      icon: BarChart3,
      title: language === "sv" ? "Ekonomiuppföljning" : "Budget Tracking",
      desc: language === "sv"
        ? "Realtidsöverblick över budget, kostnader och prognoser"
        : "Real-time overview of budget, costs and forecasts"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <img src="/controlify-logo.png" alt="Controlify" className="w-8 h-8 object-contain" />
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
                Controlify
              </span>
            </div>
            
            <div className="flex items-center gap-4">
              <button
                onClick={() => setLanguage(language === "sv" ? "en" : "sv")}
                className="text-sm text-slate-600 hover:text-slate-900"
                data-testid="lang-toggle"
              >
                {language === "sv" ? "EN" : "SV"}
              </button>
              
              {user ? (
                <Link to="/dashboard">
                  <Button data-testid="goto-dashboard-btn">{t("dashboard")}</Button>
                </Link>
              ) : (
                <Link to="/login">
                  <Button data-testid="login-btn">{t("login")}</Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: "url('/controlify-hero.png')",
          }}
        >
          <div className="absolute inset-0 bg-slate-900/70"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="max-w-2xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Barlow Condensed' }}>
              {language === "sv" 
                ? "Controlify — Projekthanteringssystem för byggbranschen" 
                : "Controlify — Project Management for Construction"}
            </h1>
            <p className="text-lg sm:text-xl text-slate-200 mb-8">
              {language === "sv"
                ? "Modernt, mobile-first SaaS-system anpassat för nordiska byggföretag. Spårbarhet, KMA och ekonomi i ett och samma verktyg."
                : "Modern, mobile-first SaaS system designed for Nordic construction companies. Traceability, safety and economy in one tool."}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/login">
                <Button size="lg" className="btn-touch w-full sm:w-auto" data-testid="hero-login-btn">
                  {language === "sv" ? "Logga in" : "Log in"}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              {language === "sv" ? "Allt du behöver — samlat i Controlify" : "Everything you need — gathered in Controlify"}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {language === "sv"
                ? "Designat för yrkesarbetare, platschefer och projektledare"
                : "Designed for workers, site managers and project managers"}
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <div key={i} className="p-6 rounded-lg border border-slate-200 hover:border-primary hover:shadow-md transition-all">
                <feature.icon className="w-10 h-10 text-primary mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  {feature.title}
                </h3>
                <p className="text-slate-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile Section */}
      <section className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Smartphone className="w-5 h-5 text-primary" />
                <span className="text-sm font-medium text-primary uppercase tracking-wide">Mobile-First</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Barlow Condensed' }}>
                {language === "sv" 
                  ? "Byggd för byggarbetsplatsen" 
                  : "Built for the construction site"}
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                {language === "sv"
                  ? "Stora knappar, minimalt antal klick och snabb tidrapportering. Fungerar även offline."
                  : "Large buttons, minimal clicks and fast time reporting. Works offline too."}
              </p>
              <ul className="space-y-3">
                {[
                  language === "sv" ? "Tidrapport på 10 sekunder" : "Time report in 10 seconds",
                  language === "sv" ? "Foto direkt i aktivitet" : "Photo directly in activity",
                  language === "sv" ? "Offline-stöd med synk" : "Offline support with sync",
                  language === "sv" ? "Stora touchytor" : "Large touch targets"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img 
                src="/controlify-hero.png"
                alt="Construction worker using phone"
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { value: "30%", label: language === "sv" ? "Mindre admin" : "Less admin" },
              { value: "100%", label: language === "sv" ? "Spårbarhet" : "Traceability" },
              { value: "10s", label: language === "sv" ? "Tidrapport" : "Time report" },
              { value: "24/7", label: language === "sv" ? "Tillgängligt" : "Available" }
            ].map((stat, i) => (
              <div key={i}>
                <div className="text-3xl sm:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  {stat.value}
                </div>
                <div className="text-slate-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="kontakt" className="py-16 sm:py-24 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              {language === "sv" 
                ? "Redo att digitalisera dina byggprojekt med Controlify?" 
                : "Ready to digitize your construction projects with Controlify?"}
            </h2>
            <p className="text-lg text-white/80">
              {language === "sv"
                ? "Kontakta oss för att komma igång."
                : "Contact us to get started."}
            </p>
          </div>

          <form 
            onSubmit={async (e) => {
              e.preventDefault();
              setSending(true);
              try {
                const res = await fetch(`${API}/contact`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify(contactForm)
                });
                if (res.ok) {
                  toast.success(language === "sv" ? "Tack! Vi återkommer så snart som möjligt." : "Thanks! We'll get back to you soon.");
                  setContactForm({ name: "", email: "", phone: "", company: "", message: "" });
                } else {
                  toast.error(language === "sv" ? "Kunde inte skicka. Försök igen." : "Could not send. Try again.");
                }
              } catch {
                toast.error(language === "sv" ? "Ett fel uppstod." : "An error occurred.");
              } finally {
                setSending(false);
              }
            }}
            className="bg-white/10 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-white/20 max-w-2xl mx-auto"
          >
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-white/90 mb-1.5">
                  {language === "sv" ? "Namn *" : "Name *"}
                </label>
                <Input
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  required
                  placeholder={language === "sv" ? "Ditt namn" : "Your name"}
                  className="bg-white/10 border-white/30 text-white placeholder:text-white/50 focus:border-white"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white/90 mb-1.5">
                  {language === "sv" ? "E-post *" : "Email *"}
                </label>
                <Input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  required
                  placeholder="namn@foretag.se"
                  className="bg-white/10 border-white/30 text-white placeholder:text-white/50 focus:border-white"
                  data-testid="contact-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white/90 mb-1.5">
                  {language === "sv" ? "Telefon" : "Phone"}
                </label>
                <Input
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  placeholder="070-123 45 67"
                  className="bg-white/10 border-white/30 text-white placeholder:text-white/50 focus:border-white"
                  data-testid="contact-phone"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white/90 mb-1.5">
                  {language === "sv" ? "Företagsnamn" : "Company"}
                </label>
                <Input
                  value={contactForm.company}
                  onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                  placeholder={language === "sv" ? "Ditt företag AB" : "Your Company Ltd"}
                  className="bg-white/10 border-white/30 text-white placeholder:text-white/50 focus:border-white"
                  data-testid="contact-company"
                />
              </div>
            </div>
            <div className="mb-5">
              <label className="block text-sm font-medium text-white/90 mb-1.5">
                {language === "sv" ? "Meddelande *" : "Message *"}
              </label>
              <textarea
                value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                required
                rows={4}
                placeholder={language === "sv" ? "Berätta vad vi kan hjälpa dig med..." : "Tell us how we can help..."}
                className="w-full rounded-md bg-white/10 border border-white/30 text-white placeholder:text-white/50 focus:border-white focus:outline-none focus:ring-1 focus:ring-white px-3 py-2 text-sm resize-none"
                data-testid="contact-message"
              />
            </div>
            <Button 
              type="submit" 
              size="lg" 
              variant="secondary" 
              className="w-full sm:w-auto btn-touch"
              disabled={sending}
              data-testid="contact-submit-btn"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {language === "sv" ? "Skickar..." : "Sending..."}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  {language === "sv" ? "Skicka meddelande" : "Send message"}
                </>
              )}
            </Button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/controlify-logo.png" alt="Controlify" className="w-6 h-6 object-contain" />
              <span className="text-slate-400">Controlify</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <span>&copy; 2025 Controlify</span>
              <a href="#" className="hover:text-slate-300">
                {language === "sv" ? "Integritet" : "Privacy"}
              </a>
              <a href="#" className="hover:text-slate-300">
                {language === "sv" ? "Villkor" : "Terms"}
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
